import { forwardRef, useImperativeHandle, useEffect, useState, useRef } from "react";
import { useThree, useLoader, useFrame } from "@react-three/fiber";
import { TextureLoader, VideoTexture, EquirectangularReflectionMapping, Vector3, Mesh, PerspectiveCamera, Scene, SRGBColorSpace, LinearFilter } from "three";
import { OrbitControls as OrbitControlsImpl } from "three-stdlib";

interface CameraBackgroundProps {
	url: string;
	showVideo: boolean;
	onVideoLoad: (videoTexture: VideoTexture) => void;
	onVideoError: () => void;
	onTryVideoStream: () => void;
}

export interface ICameraBackground {
	getVideoTexture: () => VideoTexture | null;
	getDeviceCount: () => number;
	toggleCamera: () => void;
}

const CameraBackground = forwardRef<ICameraBackground, CameraBackgroundProps>(({ url, showVideo, onVideoLoad, onVideoError, onTryVideoStream }, ref) => {
	const mediaStreamRef = useRef<MediaStream | null>(null); // Referenz statt State

	const texture = useLoader(TextureLoader, url);

	const { controls }: { controls: OrbitControlsImpl; scene: Scene } = useThree();

	const videoTextureRef = useRef<VideoTexture | null>(null);
	const meshRef = useRef<Mesh>(null!);
	const [video] = useState(() => document.createElement("video"));

	const distanceFromCamera = 1000;
	const [activeMap, setActiveMap] = useState(texture);
	const [activeCamera, setActiveCamera] = useState<"user" | "environment">("environment");
	const [devices, SetDevices] = useState<MediaDeviceInfo[]>([]);

	useImperativeHandle(ref, () => ({
		getVideoTexture: () => videoTextureRef.current,
		getDeviceCount: () => devices.length,
		toggleCamera: () => {
			const nextCamera = activeCamera === "environment" ? "user" : "environment";
			setActiveCamera(nextCamera);
		},
	}));

	const adjustCameraSettings = async (track: MediaStreamTrack) => {
		const capabilities = track.getCapabilities();
		const constraints: MediaTrackConstraints = { advanced: [] };

		// Zoom setzen, falls unterstützt
		if ("zoom" in capabilities) {
			constraints.advanced?.push({ zoom: 1 } as Partial<MediaTrackConstraintSet>); // Erhöhe Zoom auf 2x //capabilities.zoom?.min ||
		}

		// Helligkeit setzen (nur falls von Kamera unterstützt)
		if ("brightness" in capabilities) {
			constraints.advanced?.push({ brightness: 0.75 } as Partial<MediaTrackConstraintSet>); // Reduziere Helligkeit auf 50%
		}

		// Kontrast setzen (falls möglich)
		if ("contrast" in capabilities) {
			constraints.advanced?.push({ contrast: 1.2 } as Partial<MediaTrackConstraintSet>); // Erhöhe Kontrast
		}

		// Weißabgleich auf automatisch setzen (falls möglich)
		if ("whiteBalanceMode" in capabilities) {
			constraints.advanced?.push({ whiteBalanceMode: "continuous" } as Partial<MediaTrackConstraintSet>);
		}

		//Exposure
		if ("exposureMode" in capabilities) {
			constraints.advanced?.push({ exposureMode: "continuous" } as Partial<MediaTrackConstraintSet>);
		}

		//ExposureCompensation
		if ("exposureCompensation" in capabilities) {
			constraints.advanced?.push({ exposureCompensation: 1 } as Partial<MediaTrackConstraintSet>);
		}

		//ExposureTime
		if ("exposureTime" in capabilities) {
			constraints.advanced?.push({ exposureTime: 1 } as Partial<MediaTrackConstraintSet>);
		}

		// Fokusabstand setzen (falls unterstützt)
		if ("focusDistance" in capabilities) {
			constraints.advanced?.push({ focusDistance: 1 } as Partial<MediaTrackConstraintSet>); // Fokus näher setzen
		}

		// Konfiguration anwenden
		await track.applyConstraints(constraints);
	};

	const setupVideoStream = async (facingMode: "user" | "environment") => {
		if (typeof navigator === "undefined" || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
			return;
		}
		// Teile der CanvasComponent mit, dass geladen wird
		onTryVideoStream();

		try {
			if (mediaStreamRef.current) {
				mediaStreamRef.current.getTracks().forEach((track) => track.stop());
			}

			const stream = await navigator.mediaDevices.getUserMedia({
				audio: false,
				video: {
					facingMode,
					width: { min: 1024, ideal: 1920, max: 1920 },
					height: { min: 768, ideal: 1080, max: 1080 },
					frameRate: { ideal: 30, max: 30 },
				},
			});

			mediaStreamRef.current = stream;

			const track = stream.getVideoTracks()[0];

			await adjustCameraSettings(track);

			const _devices = await navigator.mediaDevices.enumerateDevices();
			// Check ob mehr als eine Camera verfügbar ist
			const videoDevices = _devices.filter((device) => device.kind === "videoinput");
			SetDevices(videoDevices);

			video.srcObject = stream;
			video.autoplay = true;
			video.muted = true;
			video.loop = true;
			// Bei erneutem Switch zum Video ist das video bereits geladen und die textur erstellt daher kann direkt darauf getogglet werden
			// if (videoTextureRef.current) {
			// 	setActiveMap(videoTextureRef.current);
			// }

			video.removeEventListener("loadeddata", onVideoLoaded);
			video.addEventListener("loadeddata", onVideoLoaded);

			video.load();
			video.play();

			// console.log("=> videoDevices", videoDevices);
		} catch (e) {
			console.warn(`Could not access ${facingMode} camera.`, (e as unknown as any).message);
			setActiveMap(texture);
			onVideoError();
		}
	};

	const onVideoLoaded = () => {
		if (!videoTextureRef.current) {
			videoTextureRef.current = new VideoTexture(video);
			videoTextureRef.current.colorSpace = SRGBColorSpace;
			videoTextureRef.current.minFilter = LinearFilter;
			videoTextureRef.current.magFilter = LinearFilter;
			videoTextureRef.current.generateMipmaps = false;

			videoTextureRef.current.mapping = EquirectangularReflectionMapping;
		}

		setActiveMap(videoTextureRef.current);
		onVideoLoad(videoTextureRef.current);
	};

	// Kamera stoppen
	const stopCamera = () => {
		// console.log("stop camera", mediaStream);

		setActiveMap(texture);
		video.pause();

		if (mediaStreamRef.current) {
			mediaStreamRef.current.getTracks().forEach((track) => {
				track.stop(); // Stoppt den Track

				if (mediaStreamRef.current) {
					mediaStreamRef.current.removeTrack(track); // Entfernt den Track explizit
				}
			});

			mediaStreamRef.current = null;
		}

		// Warte kurz, bevor srcObject entfernt wird
		setTimeout(() => {
			video.srcObject = null;
			video.load();
		}, 100);

		videoTextureRef.current = null;
	};

	useEffect(() => {
		if (showVideo) {
			setupVideoStream(activeCamera);
		} else {
			stopCamera();
		}

		return () => {
			stopCamera();

			texture.dispose();
		};
	}, [showVideo, activeCamera, texture]);

	useEffect(() => {
		const handleBackButton = () => {
			stopCamera();
		};

		window.addEventListener("popstate", handleBackButton);

		return () => {
			window.removeEventListener("popstate", handleBackButton);
		};
	}, []);

	useFrame(({ size }) => {
		if (!controls) return;

		// Berechne das Seitenverhältnis des Videos oder des Bildes
		const aspectRatio = showVideo && video.videoWidth ? video.videoWidth / video.videoHeight : texture.image.width / texture.image.height;
		const canvasAspect = size.width / size.height;

		// Berechne die Höhe der Plane basierend auf dem FOV und der Entfernung zur Kamera
		const vFov = ((controls.object as PerspectiveCamera).fov * Math.PI) / 180; // Vertikales FOV in Radiant
		const heightAtDistance = 2 * Math.abs(distanceFromCamera) * Math.tan(vFov / 2); // Höhe der Plane bei der gegebenen Distanz
		const widthAtDistance = heightAtDistance * canvasAspect; // Breite der Plane bei der gegebenen Distanz

		// Bestimme die Skalierung so, dass das Video/Bild den gesamten Canvas füllt
		if (aspectRatio < canvasAspect) {
			meshRef.current.scale.set(widthAtDistance, widthAtDistance / aspectRatio, 1);
		} else {
			meshRef.current.scale.set(heightAtDistance * aspectRatio, heightAtDistance, 1);
		}

		// Zentriere die Plane relativ zur Kamera
		const cameraDirection = new Vector3();
		controls.object.getWorldDirection(cameraDirection);
		meshRef.current.position.copy(controls.object.position).add(cameraDirection.multiplyScalar(distanceFromCamera));

		// Verhindere, dass sich die Plane dreht
		meshRef.current.quaternion.copy(controls.object.quaternion);
	});

	return (
		<mesh ref={meshRef} layers={0}>
			<planeGeometry args={[1, 1]} />
			<meshBasicMaterial map={activeMap} depthWrite={false} toneMapped={false} />
		</mesh>
	);
});

CameraBackground.displayName = "CameraBackground";

export default CameraBackground;
